import React, { useMemo } from 'react';
import * as THREE from 'three';
import { SNAP_COLORS } from '../hooks/useSnap';

/**
 * Visuelles Feedback für aktiven Snap-Punkt.
 * Zeigt verschiedene Symbole je nach Snap-Typ.
 */
export function SnapIndicator({ snap, size = 150 }) {
  if (!snap) return null;

  const { type, x, y, color = SNAP_COLORS[type] || '#ffffff' } = snap;

  // Geometrie basierend auf Snap-Typ
  const geometry = useMemo(() => {
    switch (type) {
      case 'origin':
        // Kreuz für Origin
        return <OriginMarker size={size} />;
      case 'end':
        // Quadrat für Endpunkt
        return <SquareMarker size={size} />;
      case 'mid':
        // Dreieck für Mittelpunkt
        return <TriangleMarker size={size} />;
      case 'int':
        // X für Schnittpunkt
        return <CrossMarker size={size} />;
      case 'perp':
        // Rechter Winkel für Perpendicular
        return <PerpMarker size={size} />;
      case 'near':
        // Kreis für Near
        return <CircleMarker size={size} />;
      default:
        // Kleiner Punkt für Grid
        return <DotMarker size={size * 0.5} />;
    }
  }, [type, size]);

  return (
    <group position={[x, y, 10]}>
      {React.cloneElement(geometry, { color })}
    </group>
  );
}

// Marker-Komponenten

function OriginMarker({ size, color }) {
  const halfSize = size / 2;
  const points = useMemo(() => [
    // Horizontale Linie
    new THREE.Vector3(-halfSize, 0, 0),
    new THREE.Vector3(halfSize, 0, 0),
  ], [halfSize]);

  const points2 = useMemo(() => [
    // Vertikale Linie
    new THREE.Vector3(0, -halfSize, 0),
    new THREE.Vector3(0, halfSize, 0),
  ], [halfSize]);

  return (
    <group>
      <line>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={2}
            array={new Float32Array(points.flatMap(p => [p.x, p.y, p.z]))}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color={color} linewidth={2} />
      </line>
      <line>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={2}
            array={new Float32Array(points2.flatMap(p => [p.x, p.y, p.z]))}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color={color} linewidth={2} />
      </line>
    </group>
  );
}

function SquareMarker({ size, color }) {
  const halfSize = size / 2;
  const points = useMemo(() => [
    new THREE.Vector3(-halfSize, -halfSize, 0),
    new THREE.Vector3(halfSize, -halfSize, 0),
    new THREE.Vector3(halfSize, halfSize, 0),
    new THREE.Vector3(-halfSize, halfSize, 0),
    new THREE.Vector3(-halfSize, -halfSize, 0), // Close
  ], [halfSize]);

  return (
    <line>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={5}
          array={new Float32Array(points.flatMap(p => [p.x, p.y, p.z]))}
          itemSize={3}
        />
      </bufferGeometry>
      <lineBasicMaterial color={color} linewidth={2} />
    </line>
  );
}

function TriangleMarker({ size, color }) {
  const halfSize = size / 2;
  const height = halfSize * Math.sqrt(3);
  const points = useMemo(() => [
    new THREE.Vector3(0, halfSize, 0),
    new THREE.Vector3(-halfSize, -height / 2, 0),
    new THREE.Vector3(halfSize, -height / 2, 0),
    new THREE.Vector3(0, halfSize, 0), // Close
  ], [halfSize, height]);

  return (
    <line>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={4}
          array={new Float32Array(points.flatMap(p => [p.x, p.y, p.z]))}
          itemSize={3}
        />
      </bufferGeometry>
      <lineBasicMaterial color={color} linewidth={2} />
    </line>
  );
}

function CrossMarker({ size, color }) {
  const halfSize = size / 2;
  const points1 = useMemo(() => [
    new THREE.Vector3(-halfSize, -halfSize, 0),
    new THREE.Vector3(halfSize, halfSize, 0),
  ], [halfSize]);

  const points2 = useMemo(() => [
    new THREE.Vector3(-halfSize, halfSize, 0),
    new THREE.Vector3(halfSize, -halfSize, 0),
  ], [halfSize]);

  return (
    <group>
      <line>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={2}
            array={new Float32Array(points1.flatMap(p => [p.x, p.y, p.z]))}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color={color} linewidth={2} />
      </line>
      <line>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={2}
            array={new Float32Array(points2.flatMap(p => [p.x, p.y, p.z]))}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color={color} linewidth={2} />
      </line>
    </group>
  );
}

function PerpMarker({ size, color }) {
  const halfSize = size / 2;
  // Rechter Winkel Symbol
  const points = useMemo(() => [
    new THREE.Vector3(-halfSize, 0, 0),
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(0, halfSize, 0),
  ], [halfSize]);

  // Kleines Quadrat in der Ecke
  const cornerSize = halfSize * 0.3;
  const corner = useMemo(() => [
    new THREE.Vector3(0, cornerSize, 0),
    new THREE.Vector3(cornerSize, cornerSize, 0),
    new THREE.Vector3(cornerSize, 0, 0),
  ], [cornerSize]);

  return (
    <group>
      <line>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={3}
            array={new Float32Array(points.flatMap(p => [p.x, p.y, p.z]))}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color={color} linewidth={2} />
      </line>
      <line>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={3}
            array={new Float32Array(corner.flatMap(p => [p.x, p.y, p.z]))}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color={color} linewidth={2} />
      </line>
    </group>
  );
}

function CircleMarker({ size, color }) {
  const segments = 24;
  const radius = size / 2;
  const points = useMemo(() => {
    const pts = [];
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      pts.push(new THREE.Vector3(
        Math.cos(angle) * radius,
        Math.sin(angle) * radius,
        0
      ));
    }
    return pts;
  }, [radius, segments]);

  return (
    <line>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={segments + 1}
          array={new Float32Array(points.flatMap(p => [p.x, p.y, p.z]))}
          itemSize={3}
        />
      </bufferGeometry>
      <lineBasicMaterial color={color} linewidth={2} />
    </line>
  );
}

function DotMarker({ size, color }) {
  return (
    <mesh>
      <circleGeometry args={[size / 2, 16]} />
      <meshBasicMaterial color={color} />
    </mesh>
  );
}

export default SnapIndicator;
